/**
 * CrmDetailLayout — reusable shell for CRM detail pages.
 *
 * Usage:
 *   <CrmDetailLayout
 *     avatar={<img .../>}
 *     title="Insular Square"
 *     badges={[<span key="type">Contractor</span>]}
 *     chips={[{ icon: MdBusiness, label: 'Aircraft Hangar' }, { label: '🌏 PH' }]}
 *     actions={<><Button>Assign to</Button>...</>}
 *     tabs={TABS}
 *     activeTab={activeTab}
 *     onTabChange={switchTab}
 *   >
 *     {tab content}
 *   </CrmDetailLayout>
 */
import React from 'react';
import PropTypes from 'prop-types';

// ── CrmDetailLayout ───────────────────────────────────────────────────────────
const CrmDetailLayout = ({
  avatar,
  title,
  badges = [],
  chips  = [],
  actions,
  tabs = [],
  activeTab,
  onTabChange,
  children,
}) => (
  <div className="tw-flex tw-flex-col tw-min-h-screen tw-bg-slate-50/60">

    {/* ── Header ── */}
    <div className="tw-bg-white tw-border-b tw-border-slate-200 tw-sticky tw-top-0 tw-z-30 tw-shadow-sm">
      <div className="tw-px-6 tw-pt-4 tw-pb-0">

        {/* Entity row */}
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-mb-3">

          {/* Avatar + Identity */}
          <div className="tw-flex tw-items-center tw-gap-3.5 tw-min-w-0">
            {avatar && (
              <div className="tw-w-11 tw-h-11 tw-rounded-lg tw-border tw-border-slate-200 tw-overflow-hidden tw-shadow-sm tw-bg-slate-50 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
                {avatar}
              </div>
            )}
            <div className="tw-min-w-0">
              {/* Name + type badges */}
              <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-mb-1">
                <h1 className="tw-text-[17px] tw-font-bold tw-text-slate-900 tw-leading-tight tw-tracking-tight">
                  {title || '—'}
                </h1>
                {badges.map((b, i) => (
                  <React.Fragment key={i}>{b}</React.Fragment>
                ))}
              </div>
              {/* Metadata chips */}
              {chips.length > 0 && (
                <div className="tw-flex tw-items-center tw-gap-x-4 tw-gap-y-1 tw-flex-wrap tw-mt-0.5">
                  {chips.map((c, i) => {
                    const Icon    = c.icon;
                    const content = (
                      <>
                        {Icon && <Icon size={12} className="tw-text-slate-400 tw-shrink-0" />}
                        <span className="tw-truncate tw-max-w-[200px]">{c.label}</span>
                      </>
                    );
                    return c.href ? (
                      <a key={i} href={c.href} target="_blank" rel="noreferrer"
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[12px] tw-text-blue-500 hover:tw-underline tw-font-medium">
                        {content}
                      </a>
                    ) : (
                      <span key={i} className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[12px] tw-text-slate-500 tw-font-medium">
                        {content}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          {actions && (
            <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0 tw-pt-0.5">
              {actions}
            </div>
          )}
        </div>

        {/* Tab bar */}
        {tabs.length > 0 && (
          <nav className="tw-flex tw-items-end tw-gap-0 tw-overflow-x-auto tw-no-scrollbar">
            {tabs.map(tab => {
              const isActive = activeTab === tab.key;
              const accent   = tab.accent || '#3b82f6';
              const Icon     = tab.Icon;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => onTabChange?.(tab.key)}
                  style={{ borderBottomColor: isActive ? accent : 'transparent' }}
                  className={[
                    'tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2.5 tw-text-[13px] tw-font-medium',
                    'tw-border-0 tw-border-b-2 tw-cursor-pointer tw-whitespace-nowrap tw-bg-transparent',
                    'tw-transition-all tw-duration-150',
                    isActive
                      ? 'tw-text-slate-900'
                      : 'tw-text-slate-400 hover:tw-text-slate-700 hover:tw-bg-slate-50 tw-rounded-t-sm',
                  ].join(' ')}
                >
                  {Icon && (
                    <Icon size={13} style={{ color: isActive ? accent : undefined }} />
                  )}
                  {tab.label}
                  {tab.count != null && tab.count > 0 && (
                    <span className={`tw-text-[10px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-leading-none ${
                      isActive ? 'tw-bg-blue-50 tw-text-blue-600' : 'tw-bg-slate-100 tw-text-slate-400'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        )}
      </div>
    </div>

    {/* ── Content ── */}
    <div className="tw-flex-1 tw-px-6 tw-pt-4 tw-pb-8">
      {children}
    </div>
  </div>
);

CrmDetailLayout.propTypes = {
  avatar:      PropTypes.node,
  title:       PropTypes.string,
  badges:      PropTypes.array,
  chips:       PropTypes.arrayOf(PropTypes.shape({ icon: PropTypes.elementType, label: PropTypes.any })),
  actions:     PropTypes.node,
  tabs:        PropTypes.array,
  activeTab:   PropTypes.string,
  onTabChange: PropTypes.func,
  children:    PropTypes.node,
};

export default CrmDetailLayout;
